/**
 * AudioPlayer Component
 * 
 * Uses Vidstack for audio playback on all platforms (web, iOS, Android).
 * 
 * Supports:
 * - HLS streaming from Vimeo
 * - Offline playback from local files
 * - Lock screen / notification controls via Web MediaSession API
 */

import { getAddOwnProduct, getVimeoAudioStream, type AudioStreamResponse } from "@/api/api";
import { cn } from "@/components/utils/utils";
import { useCourseProgress, useVideoProgress } from "@/context/StorageContext";
import { useUnifiedMediaSession } from "@/hooks/useUnifiedMediaSession";
import { useQuery } from "@tanstack/react-query";
import type { MediaPlayerInstance, MediaPlayEvent } from "@vidstack/react";
// eslint-disable-next-line no-duplicate-imports
import { MediaPlayer, MediaProvider } from "@vidstack/react";
import { DefaultAudioLayout, defaultLayoutIcons } from "@vidstack/react/player/layouts/default";
import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/audio.css";
import { useRef, useCallback, useEffect, useState } from "react";
import { waitForDuration } from "./MultimediaCard.utils";
import { generateMediaId, updateLastAccessed } from "@/utils/storage/offlineVideosStorage";
import type { ProductDataMultimedia } from "./types";
import { getLocalAudioUri } from "@/utils/audioDownloadServiceFactory";
import * as Sentry from "@sentry/capacitor";
import { Toast } from "@capacitor/toast";
import {
	useMediaEngine,
	type MediaEngineCallbacks,
} from "@/utils/mediaEngine";
import { useAudioSession } from "@/hooks/useAudioSession";
import { LogoLtcaIcon } from "@/components/icons/logoLtca";

export type AudioPlayerOpenState = "AUDIO_V2" | false;

export const AudioPlayer = ({
	audioContent,
	open,
	productData,
	courseClassesId,
	setOpen,
}: {
	audioContent:
	| { audio_source: "vimeo_source"; vimeoId: number }
	| { audio_source: "local_memory" }
	| { audio_source: "unknown" };
	open: AudioPlayerOpenState;
	productData: ProductDataMultimedia;
	courseClassesId: string;
	setOpen: (open: AudioPlayerOpenState) => void;
}): JSX.Element => {
	const { productSlug = "", productName = "", productId = "", productType = "unknown", authors = [] } = productData || {};

	const { markAsCompleted } = useCourseProgress();
	const audioPlayerRef = useRef<MediaPlayerInstance>(null);

	const {
		saveVideoProgress,
		shouldResumeFromSavedPosition,
		clearVideoProgress,
	} = useVideoProgress();

	const [audioError, setAudioError] = useState<string | null>(null);
	const [localAudioUri, setLocalAudioUri] = useState<string | null>(null);
	// Track if we've finished checking for local audio (to prevent HLS player showing before check completes)
	const [localAudioCheckComplete, setLocalAudioCheckComplete] = useState(false);

	// ============================================
	// Unified Media Session (lock screen controls)
	// ============================================

	const { updatePlaybackState, updatePositionState } = useUnifiedMediaSession(
		audioPlayerRef,
		{
			title: productName || "LTCA Audio",
			artist: "Akademia LTCA",
			album: "Kursy LTCA",
		},
		open === "AUDIO_V2"
	);

	// ============================================
	// Refs for stable callbacks (avoid stale closures)
	// ============================================

	const updatePlaybackStateRef = useRef(updatePlaybackState);
	updatePlaybackStateRef.current = updatePlaybackState;

	// ============================================
	// Media Engine Callbacks
	// ============================================

	const engineCallbacks: MediaEngineCallbacks = {
		onStateChange: (state): void => {
			console.log("[AudioPlayer] Engine state changed:", state);
			if (state === 'playing') {
				updatePlaybackStateRef.current('playing');
			} else if (state === 'paused') {
				updatePlaybackStateRef.current('paused');
			} else if (state === 'ended') {
				updatePlaybackStateRef.current('none');
			}
		},
		onError: (error): void => {
			console.error("[AudioPlayer] Engine error:", error);
			setAudioError("Nie udało się załadować audio. Spróbuj ponownie.");

			Sentry.captureException(error, {
				tags: { component: 'AudioPlayer' },
				contexts: {
					audio: {
						source: audioContent.audio_source,
						productId,
						courseClassesId
					}
				}
			});

			void Toast.show({
				text: "Błąd ładowania audio",
				duration: "long",
				position: "bottom"
			});
		},
	};

	// ============================================
	// Media Engine Setup (Vidstack)
	// ============================================

	const {
		attachPlayer,
		handleCanPlay,
		handlePlay: engineHandlePlay,
		handlePause: engineHandlePause,
		handleEnded: engineHandleEnded,
		handleError: engineHandleError,
		handleTimeUpdate: engineHandleTimeUpdate,
		platform,
	} = useMediaEngine(
		{
			mediaType: "audio",
			backgroundMode: true,
			showNotification: true,
		},
		engineCallbacks
	);

	const safeUpdatePlaybackState = useCallback((state: "playing" | "paused" | "none") => {
		if (audioPlayerRef.current) {
			updatePlaybackState(state);
		}
	}, [updatePlaybackState]);

	// Attach player to engine when available
	useEffect(() => {
		if (open === "AUDIO_V2" && audioPlayerRef.current) {
			attachPlayer(audioPlayerRef.current);
		}
	}, [open, attachPlayer]);

	// ============================================
	// Queries
	// ============================================

	const { refetch: addOwnProduct } = useQuery<unknown>({
		queryKey: ["addOwnProduct", productSlug],
		queryFn: () => getAddOwnProduct(productSlug),
		enabled: false,
	});

	const isVimeo = audioContent.audio_source === "vimeo_source";
	const vimeoId = isVimeo ? audioContent.vimeoId : undefined;

	const { data: audioStreamData, isLoading: isLoadingAudioStream } = useQuery<AudioStreamResponse | undefined>({
		queryKey: ["vimeoAudioStream", vimeoId],
		queryFn: () => getVimeoAudioStream(vimeoId!.toString()),
		enabled: isVimeo && !!vimeoId && open === "AUDIO_V2",
	});

	// ============================================
	// iOS Audio Session (interruption handling)
	// ============================================

	const wasPlayingBeforeInterruption = useRef(false);

	useAudioSession(
		{
			onInterruptionBegan: () => {
				console.log("[AudioPlayer] iOS interruption began - pausing");
				wasPlayingBeforeInterruption.current = audioPlayerRef.current?.paused === false;
				audioPlayerRef.current?.pause().catch(console.error);
				safeUpdatePlaybackState("paused");
			},
		onInterruptionEnded: () => {
			console.log("[AudioPlayer] iOS interruption ended");
			if (wasPlayingBeforeInterruption.current) {
					audioPlayerRef.current?.play().catch(console.error);
					safeUpdatePlaybackState("playing");
				}
				wasPlayingBeforeInterruption.current = false;
			},
			onHeadphonesDisconnected: () => {
				console.log("[AudioPlayer] Headphones disconnected - pausing");
				audioPlayerRef.current?.pause().catch(console.error);
				safeUpdatePlaybackState("paused");
			},
		},
		open === "AUDIO_V2" && platform === "ios"
	);

	// ============================================
	// Event Handlers
	// ============================================

	const handlePlay = useCallback(
		async (event: MediaPlayEvent): Promise<void> => {
			console.log("[AudioPlayer] onPlay", event);
			safeUpdatePlaybackState("playing");
			engineHandlePlay();

			try {
				await markAsCompleted(productId, courseClassesId);
				await addOwnProduct();
			} catch (error) {
				console.log("[AudioPlayer] error marking complete", error);
			}
		},
		[markAsCompleted, productId, courseClassesId, addOwnProduct, safeUpdatePlaybackState, engineHandlePlay]
	);

	const handlePause = useCallback(() => {
		safeUpdatePlaybackState("paused");
		engineHandlePause();
	}, [safeUpdatePlaybackState, engineHandlePause]);

	const handleTimeUpdate = useCallback(
		async (event: { currentTime: number }) => {
			const currentTime = event.currentTime;
			const duration = audioPlayerRef.current?.duration ?? 0;

			if (duration > 0) {
				await saveVideoProgress(productId, courseClassesId, currentTime, duration);
			}

			engineHandleTimeUpdate();
			updatePositionState();
		},
		[saveVideoProgress, productId, courseClassesId, engineHandleTimeUpdate, updatePositionState]
	);

	const handleLoadedMetadata = useCallback(async () => {
		console.log("🎵 [AudioPlayer] Metadata loaded");
		const player = audioPlayerRef.current;
		if (player) {
			try {
				const duration = await waitForDuration(player);
				console.log("✅ [AudioPlayer] Duration ready:", duration);

				const resumeTime = shouldResumeFromSavedPosition(productId, courseClassesId, duration);

				if (resumeTime !== null) {
					console.log(`🎵 [AudioPlayer] Resuming from ${Math.round(resumeTime)}s`);
					player.currentTime = resumeTime;
				}

				handleCanPlay();
			} catch (error) {
				console.error("❌ [AudioPlayer] Error waiting for duration:", error);
			}
		}
	}, [shouldResumeFromSavedPosition, productId, courseClassesId, handleCanPlay]);

	const handleEnded = useCallback(async () => {
		safeUpdatePlaybackState("none");
		engineHandleEnded();
		await clearVideoProgress(productId, courseClassesId);
	}, [clearVideoProgress, productId, courseClassesId, safeUpdatePlaybackState, engineHandleEnded]);

	const handlePlayerError = useCallback(async (error: unknown) => {
		console.error("[AudioPlayer] Error:", error);
		const errorMessage = "Nie udało się załadować audio. Spróbuj ponownie.";
		setAudioError(errorMessage);
		engineHandleError(error);

		Sentry.captureException(error, {
			tags: { component: 'AudioPlayer' },
			contexts: {
				audio: {
					source: audioContent.audio_source,
					productId,
					courseClassesId
				}
			}
		});

		await Toast.show({
			text: "Błąd ładowania audio",
			duration: "long",
			position: "bottom"
		});
	}, [audioContent.audio_source, productId, courseClassesId, engineHandleError]);

	// Safe wrapper functions
	const safeHandlePlay = useCallback(
		async (event?: MediaPlayEvent): Promise<void> => {
			if (!event) {
				console.warn("[AudioPlayer] onPlay called with undefined event");
				return;
			}
			await handlePlay(event);
		},
		[handlePlay]
	);

	const safeHandleTimeUpdate = useCallback(
		async (event?: { currentTime: number }): Promise<void> => {
			if (!event || typeof event.currentTime !== 'number') {
				console.warn("[AudioPlayer] onTimeUpdate called with invalid event:", event);
				return;
			}
			await handleTimeUpdate(event);
		},
		[handleTimeUpdate]
	);

	const safeHandlePlayerError = useCallback(
		async (error?: unknown): Promise<void> => {
			if (!error) {
				console.warn("[AudioPlayer] onError called with undefined error");
				return;
			}
			await handlePlayerError(error);
		},
		[handlePlayerError]
	);

	// ============================================
	// Check for local audio file
	// ============================================

	// Ref to track if loading is in progress (survives re-renders)
	const isLoadingRef = useRef(false);

	useEffect(() => {
		// Guard: Skip if already loading or already have a valid URI
		if (isLoadingRef.current) {
			return;
		}

		if (localAudioUri) {
			return;
		}

		const checkLocalAudio = async (): Promise<void> => {
			if (open === "AUDIO_V2") {
				isLoadingRef.current = true;
				
				console.log("🔍 [AudioPlayer] Checking local audio", {
					productType,
					productId,
					courseClassesId,
					audioContent,
					platform,
				});
				try {
					const audioUri = await getLocalAudioUri(productType, productId, courseClassesId);
					console.log("🔍 [AudioPlayer] Audio URI result:", audioUri);
					setLocalAudioUri(audioUri);

					if (audioUri) {
						// Use base key format (without _audio suffix)
						const audioId = generateMediaId(productType, productId, courseClassesId);
						await updateLastAccessed(audioId);
						console.log("📂 [AudioPlayer] Playing from local storage - SUCCESS");
					} else {
						console.log("📂 [AudioPlayer] No local audio found");

						if (audioContent.audio_source === "local_memory") {
							setAudioError("Audio nie zostało znalezione w pamięci lokalnej. Spróbuj pobrać je ponownie.");
						}
					}
				} catch (error) {
					console.error("❌ [AudioPlayer] Error loading local audio:", error);
					setLocalAudioUri(null);

					if (audioContent.audio_source === "local_memory") {
						setAudioError("Błąd podczas ładowania audio z pamięci lokalnej.");
					}
				} finally {
					isLoadingRef.current = false;
					setLocalAudioCheckComplete(true);
				}
			}
		};
		checkLocalAudio().catch((error) => {
			console.error("❌ [AudioPlayer] Error in checkLocalAudio:", error);
			setLocalAudioUri(null);
			isLoadingRef.current = false;
			setLocalAudioCheckComplete(true);
		});
		// Note: audioContent excluded from deps - object reference changes on each render
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productId, productType, courseClassesId, open, audioContent.audio_source, platform, localAudioUri]);

	// ============================================
	// Logging effect
	// ============================================

	useEffect(() => {
		if (!productData || !productId || !productType) {
			console.warn("[AudioPlayer] Missing product data", { productData, productId, productType });
		}
	}, [productData, productId, productType]);

	// ============================================
	// Render
	// ============================================

	// Common player props
	const commonPlayerProps = {
		controls: false,
		playsInline: true,
		className: cn("w-full", open === "AUDIO_V2" ? "block" : "!hidden"),
		load: "visible" as const,
		preload: "auto" as const,
		ref: audioPlayerRef,
		streamType: "on-demand" as const,
		viewType: "audio" as const,
		onPlay: safeHandlePlay,
		onPause: handlePause,
		onTimeUpdate: safeHandleTimeUpdate,
		onEnded: handleEnded,
		onError: safeHandlePlayerError,
		onCanPlay: handleCanPlay,
	};

	console.log("🎵 [AudioPlayer] Rendering decision:", {
		audioContent,
		localAudioUri,
		audioStreamUrl: audioStreamData?.audioStreamUrl,
		isLoadingAudioStream,
		platform,
		willRenderVimeoHLS: audioStreamData?.audioStreamUrl && !localAudioUri,
		willRenderLocal: !!localAudioUri,
	});

	if (open !== "AUDIO_V2") {
		return <></>;
	}

	return (
		<div className="flex flex-col items-center justify-center min-h-screen bg-black p-6">
			{/* Close button */}
			<button
				className="absolute top-6 right-6 p-4 text-white text-[40px] z-50 font-extrabold"
				onClick={async () => {
					await audioPlayerRef.current?.pause();
					safeUpdatePlaybackState("none");
					setOpen(false);
				}}
			>
				✕
			</button>

			{/* Audio info */}
			<div className="text-white text-center mb-8 max-w-md">
				<LogoLtcaIcon className="h-24 w-auto mx-auto mb-6" color="white" />
				<h2 className="text-xl font-semibold mb-2">{productName || "Odsłuch audio"}</h2>
				<p className="text-gray-400 text-sm">{authors.map((author) => author.display_name).join(", ")}</p>
			</div>

			{/* Loading state - show while checking local audio or loading stream */}
			{(isLoadingAudioStream || !localAudioCheckComplete) && !localAudioUri && (
				<div className="text-white text-center">
					<p>Ładowanie audio...</p>
				</div>
			)}

			{/* Audio Player - Vimeo HLS streaming (only show after confirming no local audio) */}
			{audioStreamData?.audioStreamUrl && !localAudioUri && localAudioCheckComplete && (
				<div className="w-full max-w-md">
					<MediaPlayer
						{...commonPlayerProps}
						src={{
							src: audioStreamData.audioStreamUrl,
							type: "application/x-mpegurl",
						}}
						onLoadedData={() => {
							const player = audioPlayerRef.current;
							if (player && audioStreamData?.audioStreamUrl) {
								const resumeTime = shouldResumeFromSavedPosition(
									productId,
									courseClassesId,
									player.duration
								);

								if (resumeTime !== null) {
									console.log(`🎵 [AudioPlayer] Resuming from ${Math.round(resumeTime)}s`);
									player.currentTime = resumeTime;
								}
							}
							handleCanPlay();
						}}
					>
						<MediaProvider />
						<DefaultAudioLayout icons={defaultLayoutIcons} />
					</MediaPlayer>
				</div>
			)}

			{/* Audio Player - Local file */}
			{localAudioUri && (
				<div className="w-full max-w-md">
					<MediaPlayer
						{...commonPlayerProps}
						key={`audio-local-${productId}-${courseClassesId}`}
						src={{
							src: localAudioUri,
							type: "video/mp4"
						}}
						onSourceChange={(source) => {
							console.log("🎵 [AudioPlayer] Source changed:", source);
						}}
						onLoadedMetadata={() => {
							void handleLoadedMetadata();
						}}
						onCanPlay={() => {
							console.log("🎵 [AudioPlayer] Can play");
							handleCanPlay();
						}}
						onCanPlayThrough={() => {
							console.log("🎵 [AudioPlayer] Can play through");
						}}
						onProviderChange={(provider) => {
							console.log("🎵 [AudioPlayer] Provider changed:", provider?.type);
						}}
						onProviderSetup={() => {
							console.log("🎵 [AudioPlayer] Provider setup complete");
						}}
					>
						<MediaProvider />
						<DefaultAudioLayout icons={defaultLayoutIcons} />
					</MediaPlayer>
				</div>
			)}

			{/* Local memory audio - loading state */}
			{audioContent.audio_source === "local_memory" && !localAudioUri && (
				<div className="text-white text-center">
					<p>Ładowanie audio z pamięci lokalnej...</p>
				</div>
			)}

			{/* Error UI */}
			{audioError && (
				<div className="absolute inset-0 flex items-center justify-center bg-black/80 z-40 p-6">
					<div className="bg-white rounded-lg p-6 max-w-md">
						<h3 className="text-lg font-semibold mb-2 text-red-600">
							Błąd odtwarzania audio
						</h3>
						<p className="text-sm text-gray-700 mb-4">{audioError}</p>
						<div className="flex gap-3">
							<button
								onClick={() => {
									setAudioError(null);
									setOpen(false);
								}}
								className="flex-1 px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
							>
								Zamknij
							</button>
							<button
								onClick={() => {
									setAudioError(null);
									window.location.reload();
								}}
								className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
							>
								Odśwież stronę
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AudioPlayer;
